import type { ResponseType } from 'axios'
import { FetchMethod } from '../../models/api/fetch-method'
import { useApiClient } from './useApiClient'
import { computed, ref, toValue, type MaybeRef } from 'vue'
import { ApiRequestStatus } from '../../models/api/api-request-status'

function useGet<TOutput>(
  endpoint: MaybeRef<string>,
  options?: {
    omitOrganization?: boolean
    responseType?: ResponseType
    headers?: MaybeRef<Record<string, string> | undefined>
  },
) {
  const { omitOrganization = false, responseType, headers } = options || {}

  const { data, status: apiStatus, error, execute, responseHeaders } = useApiClient<TOutput>()

  const _load = async (input?: { query?: Query }) => {
    await execute({
      method: FetchMethod.GET,
      endpoint: toValue(endpoint),
      omitOrganization,
      responseType: responseType,
      query: input?.query,
      headers: toValue(headers),
    })
  }

  const load = async (input?: { query?: Query }) => {
    await _load(input)
  }

  const isReloading = ref(false)

  const reload = async (input?: { query?: Query }) => {
    isReloading.value = true
    await _load(input)
    isReloading.value = false
  }

  const status = computed(() => {
    if (isReloading.value) {
      return ApiRequestStatus.Reloading
    }
    return apiStatus.value
  })

  return { data, status, error, load, reload, responseHeaders }
}

export default useGet

type Query = { [key: string]: string | boolean | number | string[] }
